<div class="mapping-container view">
    @if (isLoading) {
        <div class="loading">
            <mat-spinner color="accent" diameter="40"></mat-spinner>
            <p>Loading field mappings...</p>
        </div>
    }

    @if (error) {
        <div class="error-message">
            <p>{{ error }}</p>
        </div>
    }

    @if (!isLoading) {
        <mat-card class="cardPlain">
      <div class="custom-card-header">
        <div class="section-header">
          <app-breadcrumbs [items]="breadcrumbs"></app-breadcrumbs>
          <h1 class="m-0 f-w-500">Field Mapping</h1>
          <p class="m-0 help-text">
            Map Brønnøysund fields to HubSpot properties for Shine Enrich.
          </p>
        </div>
      </div>
      <mat-card-content class="d-flex flex-col gap-24">
          <form [formGroup]="mappingForm" class="d-flex flex-col gap-24 form-no-subscripts">
          <div class="tab-content d-flex flex-col gap-16">
            <section class="tab-section d-flex flex-col gap-12">
              <div class="section-header">
                <h4 class="m-0 f-s-16 f-w-600">Mappings</h4>
                <p class="m-0 f-s-14 text-gray-100">
                  Configure additional mappings from the Norwegian Business Registry to HubSpot.
                </p>
              </div>
              <div class="mapping-instructions">
                <p>
                  <strong class="mapping-instructions__note">Note:</strong> Shine Enrich properties are created automatically. Use this page to map
                  extra registry fields into HubSpot company properties.
                </p>
                <p>
                  Add mappings one at a time. For each source field, select a HubSpot property or leave it empty to
                  skip mapping that field.
                </p>
              </div>
              <div class="overwrite-warning warning-box warning-accordion">
                <div class="warning-header">
                  <div class="warning-title-wrap">
                    <mat-icon class="warning-icon">warning</mat-icon>
                    <h4 class="overwrite-title warning-title m-0">
                        Shine Enrich can update these HubSpot properties when registry data has a value.<br/>
                        Configure overwrite behavior from HubSpot Settings.
                    </h4>
                  </div>
                  <button
                    mat-icon-button
                    type="button"
                    (click)="toggleWarning()"
                    aria-label="Toggle overwrite details"
                  >
                    <mat-icon>{{ warningExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                  </button>
                </div>
                  @if (warningExpanded) {
                      <ul>
                          @for (field of overwrittenHubspotFields; track field) {
                              <li>
                                  <strong>{{ field.hubspot }}</strong>
                                  <span>← {{ field.source }}</span>
                              </li>
                          }
                      </ul>
                  }
              </div>
              <div formArrayName="mappings" class="mappings-list">
                <table
                  mat-table
                  #mappingsTable="matTable"
                  [dataSource]="mappings.controls"
                  class="webinars-table webinars-table--inner-padding"
                >
                  <ng-container matColumnDef="sourceField">
                    <th mat-header-cell *matHeaderCellDef class="visually-hidden">Source field</th>
                    <td mat-cell *matCellDef="let mapping; let i = index" [formGroupName]="i">
                      <mat-form-field appearance="outline" class="w-100">
                        <mat-label>Source field</mat-label>
                        <input
                          type="text"
                          placeholder="Search fields..."
                          matInput
                          formControlName="sourceField"
                          [matAutocomplete]="sourceAuto"
                        />
                        <mat-autocomplete #sourceAuto="matAutocomplete" [displayWith]="displaySourceField">
                            @for (field of filterSourceFields(mapping.value.sourceField); track field) {
                                <mat-option [value]="field.value">
                                    {{ field.label }}
                                </mat-option>
                            }
                        </mat-autocomplete>
                      </mat-form-field>
                    </td>
                  </ng-container>
                  <ng-container matColumnDef="targetField">
                    <th mat-header-cell *matHeaderCellDef class="visually-hidden">HubSpot property</th>
                    <td mat-cell *matCellDef="let mapping; let i = index" [formGroupName]="i">
                      <mat-form-field appearance="outline" class="w-100">
                        <mat-label>HubSpot property</mat-label>
                        <input
                          type="text"
                          placeholder="Search properties..."
                          matInput
                          formControlName="targetField"
                          [matAutocomplete]="targetAuto"
                        />
                        <mat-autocomplete #targetAuto="matAutocomplete" [displayWith]="displayTargetField">
                            @for (prop of filterHubspotProperties(mapping.value.targetField); track prop) {
                                <mat-option [value]="prop.name">
                                    {{ prop.label }}
                                </mat-option>
                            }
                        </mat-autocomplete>
                      </mat-form-field>
                    </td>
                  </ng-container>
                  <ng-container matColumnDef="enabled">
                    <th mat-header-cell *matHeaderCellDef class="visually-hidden">Enabled</th>
                    <td mat-cell *matCellDef="let mapping; let i = index" [formGroupName]="i" class="text-center">
                      <mat-slide-toggle formControlName="enabled" color="primary" appearance="switch"></mat-slide-toggle>
                    </td>
                  </ng-container>
                  <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef class="visually-hidden"></th>
                    <td mat-cell *matCellDef="let mapping; let i = index" [formGroupName]="i" class="text-center">
                      <button mat-icon-button color="warn" (click)="removeMapping(i)" aria-label="Remove mapping">
                        <mat-icon>delete</mat-icon>
                      </button>
                    </td>
                  </ng-container>
                  <tr mat-row *matRowDef="let row; columns: ['sourceField', 'targetField', 'enabled', 'actions'];"></tr>
                </table>
                  @if (mappings.length === 0) {
                      <div class="no-mappings">
                          <p>No field mappings configured. Click "Add mapping" to create one.</p>
                      </div>
                  }
                <div class="add-mapping">
                  <button mat-stroked-button color="primary" (click)="addMapping()" type="button" class="d-inline-flex align-items-center gap-6">
                    <mat-icon>add</mat-icon>
                    Add mapping
                  </button>
                </div>
              </div>
            </section>
          </div>
          <mat-card-actions align="end" class="action-buttons p-0">
            <button
              mat-flat-button
              color="primary"
              (click)="saveMappings()"
              [disabled]="isSaving"
              type="button"
              class="d-inline-flex align-items-center gap-6"
            >
              <mat-icon>save</mat-icon>
              Save mappings
            </button>
            <button
              mat-stroked-button
              color="primary"
              (click)="loadMappings()"
              [disabled]="isLoading || isSaving"
              type="button"
              class="d-inline-flex align-items-center gap-6"
            >
              <mat-icon>refresh</mat-icon>
              Reset
            </button>
              @if (isFromSetup) {
                  <button
                          (click)="confirmAndContinue()"
                          [disabled]="isSaving"
                          class="d-inline-flex align-items-center gap-6"
                          color="primary"
                          mat-flat-button
                          type="button"
                  >
                      <mat-icon>check</mat-icon>
                      Confirm &amp; continue
                  </button>
              }
          </mat-card-actions>
        </form>
      </mat-card-content>
    </mat-card>
    }
</div>
